import { sleep } from "@grafit/shared/utils/sleep";

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 100;
const ATTEMPT_TIMEOUT_MS = 2500;
const TOTAL_TIMEOUT_MS = 5000;

interface FetchWithRetryOptions {
  /** Whole budget: a blackholed API must not pin the caller for attempts × timeout. */
  totalTimeoutMs?: number;
  attemptTimeoutMs?: number;
}

/** A 4xx other than 429 is deterministic: retrying it only delays the failure. */
function isRetryableStatus(status: number): boolean {
  return status === 429 || status >= 500;
}

interface FetchAttemptArgs {
  url: string;
  init: RequestInit | undefined;
  attempt: number;
  deadline: number;
  attemptTimeoutMs: number;
}

function canRetry({ attempt, deadline, retryDelayMs }: { attempt: number; deadline: number; retryDelayMs: number }) {
  return attempt < MAX_RETRIES && Date.now() + retryDelayMs < deadline;
}

async function fetchAttempt({ url, init, attempt, deadline, attemptTimeoutMs }: FetchAttemptArgs): Promise<Response> {
  const retryDelayMs = INITIAL_RETRY_DELAY_MS * 2 ** attempt;
  const timeoutMs = Math.max(0, Math.min(attemptTimeoutMs, deadline - Date.now()));

  try {
    const response = await fetch(url, { ...init, signal: AbortSignal.timeout(timeoutMs) });
    if (!isRetryableStatus(response.status) || !canRetry({ attempt, deadline, retryDelayMs })) {
      return response;
    }
  } catch (error) {
    if (!canRetry({ attempt, deadline, retryDelayMs })) {
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  await sleep(retryDelayMs);
  return fetchAttempt({ url, init, attempt: attempt + 1, deadline, attemptTimeoutMs });
}

export function fetchWithRetry(
  url: string,
  init?: RequestInit,
  options: FetchWithRetryOptions = {},
): Promise<Response> {
  const { totalTimeoutMs = TOTAL_TIMEOUT_MS, attemptTimeoutMs = ATTEMPT_TIMEOUT_MS } = options;
  return fetchAttempt({ url, init, attempt: 0, deadline: Date.now() + totalTimeoutMs, attemptTimeoutMs });
}
